export const MARKETPLACE_EVENT_URL_TEMPLATES: Record<string, string> = {
	TICKET_MASTER: "https://www.ticketmaster.com/event/{marketplaceEventId}",
	SEATGEEK: "https://seatgeek.com/nba-all-star-game-tickets/2-15-2026-inglewood-california-intuit-dome/nba/{marketplaceEventId}",
	VIAGOGO: "https://www.viagogo.com/event/E-{marketplaceEventId}",
	GAMETIME: "https://gametime.co/e/e/e/events/{marketplaceEventId}",
	TICKETNETWORK: "https://www.ticketnetwork.com/tickets/{marketplaceEventId}",
	GOTICKETS: "https://gotickets.com/tickets/{marketplaceEventId}",
	TICKETEVOLUTION: "https://www.events365.com/events/e/{marketplaceEventId}",
	AXS: "https://www.axs.com/events/{marketplaceEventId}",
};

const MARKETPLACE_KEY_ALIASES: Record<string, string> = {
	VIA_GOGO: "VIAGOGO",
	SEAT_GEEK: "SEATGEEK",
	GAME_TIME: "GAMETIME",
	TICKET_NETWORK: "TICKETNETWORK",
	GO_TICKETS: "GOTICKETS",
	TICKET_EVOLUTION: "TICKETEVOLUTION",
};

const MARKETPLACE_LABELS: Record<string, string> = {
	TICKET_MASTER: "Ticketmaster",
	SEATGEEK: "SeatGeek",
	VIAGOGO: "Viagogo",
	GAMETIME: "Gametime",
	TICKETNETWORK: "TicketNetwork/TickPick",
	GOTICKETS: "GoTickets",
	TICKETEVOLUTION: "TicketEvolution",
	AXS: "AXS",
	GENERIC_MP: "Email MP",
	VIVID_SEATS: "VividSeats",
	TM_THIRD_PARTY: "Ticketmaster (3rd party)",
};

const humanizeMarketplaceKey = (key: string): string =>
	key
		.split("_")
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");

export const normalizeMarketplaceEventUrlKey = (marketplace?: string | null): string | null => {
	const rawMarketplace = marketplace?.trim();

	if (!rawMarketplace) {
		return null;
	}

	return MARKETPLACE_KEY_ALIASES[rawMarketplace] ?? rawMarketplace;
};

export const getMarketplaceEventUrl = (
	marketplace?: string | null,
	marketplaceEventId?: string | null,
): string | null => {
	const normalizedMarketplace = normalizeMarketplaceEventUrlKey(marketplace);
	const normalizedEventId = marketplaceEventId?.trim();

	if (!normalizedMarketplace || !normalizedEventId) {
		return null;
	}

	const template = MARKETPLACE_EVENT_URL_TEMPLATES[normalizedMarketplace];
	if (!template) {
		return null;
	}

	return template.replace("{marketplaceEventId}", encodeURIComponent(normalizedEventId));
};

export const getMarketplaceDisplayName = (marketplace?: string | null): string => {
	const normalizedMarketplace = normalizeMarketplaceEventUrlKey(marketplace);

	if (!normalizedMarketplace) {
		return "Marketplace";
	}

	return MARKETPLACE_LABELS[normalizedMarketplace] ?? humanizeMarketplaceKey(normalizedMarketplace);
};

const MARKETPLACE_KEY_BY_ID: Record<number, string> = {
	104: "STUBHUB",
	105: "TICKET_NETWORK",
	106: "TICKET_MASTER",
	115: "VIVID_SEATS",
	116: "TICKET_EVOLUTION",
	117: "SEAT_GEEK",
	118: "TICK_PICK",
	120: "VIA_GOGO",
	121: "GAME_TIME",
	122: "FAN_XCHANGE",
	125: "AXS",
	126: "STUBHUB_INT",
	127: "GO_TICKETS",
	[-1]: "GENERIC_MP",
};

export const getMarketplaceKeyById = (marketplaceId?: number | null): string | null => {
	if (marketplaceId == null) {
		return null;
	}

	return MARKETPLACE_KEY_BY_ID[marketplaceId] ?? null;
};

export const getMarketplaceNameById = (marketplaceId?: number | null): string => {
	if (marketplaceId == null) {
		return "Marketplace";
	}

	return getMarketplaceDisplayName(MARKETPLACE_KEY_BY_ID[marketplaceId] ?? null);
};

const MARKETPLACE_ID_BY_KEY: Record<string, number> = Object.entries(MARKETPLACE_KEY_BY_ID).reduce<Record<string, number>>((acc, [id, key]) => {
	acc[key] = Number(id);
	return acc;
}, {});

export const getMarketplaceIdByName = (marketplace?: string | null): number | null => {
	if (!marketplace) {
		return null;
	}

	return MARKETPLACE_ID_BY_KEY[marketplace] ?? MARKETPLACE_ID_BY_KEY[normalizeMarketplaceEventUrlKey(marketplace) ?? ""] ?? null;
};
